import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import SocialPlatformIconV1 from "../components/SocialPlatformIconV1";

// Content Steward social publishing assistant (manual, approval-first).
// Reads the existing editorial feeds. No social tokens, auto-posting or new permissions.
const CHANNELS = {
  facebook: { name: "Facebook", url: "https://www.facebook.com/nextscenes" },
  instagram: { name: "Instagram", url: "https://www.instagram.com/nextscenes/" },
  youtube: { name: "YouTube", url: "https://www.youtube.com/@NextScenesOfficial" },
};
const SITE = "https://nextscenes.org";
const todayUTC = () => new Date().toISOString().slice(0, 10);
const clean = (value) => typeof value === "string" ? value.trim() : "";
const httpsUrl = (value) => {
  try {
    const parsed = new URL(clean(value));
    return parsed.protocol === "https:" && !parsed.username && !parsed.password
      ? parsed.toString() : "";
  } catch { return ""; }
};
const surface = {
  border: "1px solid #c9decf", borderRadius: 12, background: "#fff",
  color: "#173b2f", padding: 12, width: "100%", fontSize: 14,
};
const action = {
  border: "1px solid #bad5c3", borderRadius: 22, padding: "9px 14px",
  fontWeight: 700, color: "#125638", background: "white", cursor: "pointer",
};
function collectSources(data) {
  const sources = [{
    id:"announcement",label:"New Announcement / Original Post",
    en:{title:""},fr:{title:""},url:SITE
  }];
  const add = (id, label, en, fr, url) => {
    if (!clean(en?.title) && !clean(fr?.title)) return;
    sources.push({ id, label, en, fr, url: httpsUrl(url) });
  };
  const spotlight = data.spotlight?.spotlight || {};
  const writer = spotlight.writer || {};
  add("writer", "Writer Spotlight", {
    title: clean(writer.name), description: clean(writer.teaser),
  }, { title: clean(writer.name), description: clean(writer.teaserFr) }, SITE + "/writers");
  const book = spotlight.book || {};
  add("book", "Book Spotlight", {
    title: clean(book.title), description: clean(book.teaser),
  }, { title: clean(book.titleFr || book.title), description: clean(book.teaserFr) },
  book.linkUrl || SITE + "/books");

  const items = Array.isArray(data.schedule?.items) ? data.schedule.items : [];
  items.filter((item) => item.status === "approved" && item.dateKey <= todayUTC())
    .sort((a, b) => String(b.dateKey).localeCompare(String(a.dateKey)))
    .slice(0, 28).forEach((item) => {
      if (item.kind === "dailyWisdom") {
        const d = item.daily || {};
        // Attribution stays with the quotation; do not remove authorship or source credits.
        const attribution = clean(d.author) ? " — " + clean(d.author) : "";
        add("daily:" + item._id, "Daily Words · " + item.dateKey,
          { title: clean(d.quote) + attribution, description: clean(d.reflection) },
          { title: clean(d.quoteFr) + attribution, description: clean(d.reflectionFr) },
          SITE);
      } else if (item.kind === "weeklyMystery") {
        const w = item.weekly || {};
        add("mystery:" + item._id, "Mystery250 · " + item.dateKey,
          { title: clean(w.title), description: clean(w.teaser) },
          { title: clean(w.titleFr), description: clean(w.teaserFr) },
          SITE + "/mystery250");
      }
    });

  const enReading = data.publicEn?.featuredReading || null;
  const frReading = data.publicFr?.featuredReading || null;
  if (enReading?.isVisible || frReading?.isVisible) {
    add("reading", "Reading Spotlight",
      enReading?.isVisible ? { title: clean(enReading.title), description: clean(enReading.description) } : null,
      frReading?.isVisible ? { title: clean(frReading.title), description: clean(frReading.description) } : null,
      enReading?.href || frReading?.href || SITE + "/books");
  }
  return sources;
}
function makeCopy(source, channel, language) {
  if (source?.id === "announcement") return "";
  const part = source?.[language];
  if (!clean(part?.title)) return "";
  const french = language === "fr";
  const intro = clean(part.description);
  const visit = french ? "À découvrir sur NextScenes :" : "Discover more on NextScenes:";
  const tags = channel === "instagram" ? (french ? "\n#NextScenes #Lecture #Écriture" : "\n#NextScenes #Reading #Writing") :
    channel === "youtube" ? "\n#NextScenes" : "";
  return [clean(part.title), intro, visit + " " +
    (source.url || SITE + (french ? "/fr" : ""))].filter(Boolean).join("\n\n") + tags;
}

export default function SocialMediaKitV1({ lang = "en" }) {
  const fr = lang === "fr";
  const [feeds, setFeeds] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [channel, setChannel] = useState("facebook");
  const [language, setLanguage] = useState(fr ? "fr" : "en");
  const [sourceId, setSourceId] = useState("announcement");
  const [copy, setCopy] = useState("");
  const [headline, setHeadline] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [fbStatus, setFbStatus] = useState(null);
  const [fbVerified, setFbVerified] = useState(null);
  const [fbBusy, setFbBusy] = useState(false);
  const [fbApproved, setFbApproved] = useState(null);
  const [fbHistory, setFbHistory] = useState([]);
  const [fbError, setFbError] = useState("");
  const [fbNotice, setFbNotice] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);
  const clearImage = () => { setImageFile(null); setImageDigest(""); setImageError(""); if (fileInputRef.current) fileInputRef.current.value = ""; };
  const [imagePreview, setImagePreview] = useState("");
  const [imageDigest, setImageDigest] = useState("");
  const [imageError, setImageError] = useState("");
  useEffect(() => {
    if (!imageFile) { setImagePreview(""); setImageDigest(""); return; }
    const preview = URL.createObjectURL(imageFile);
    let active = true;
    setImagePreview(preview);
    setImageDigest("");
    imageFile.arrayBuffer().then(async bytes => {
      const digest = await crypto.subtle.digest("SHA-256", bytes);
      const hex = Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2,"0")).join("");
      if (active) setImageDigest(hex);
    }).catch(() => { if (active) setImageError("Could not verify the selected image."); });
    return () => { active = false; URL.revokeObjectURL(preview); };
  }, [imageFile]);
  const selectImage = (file) => {
    setImageError("");
    if (!file) { setImageFile(null); return; }
    if (!["image/jpeg","image/png"].includes(file.type) || file.size > 5 * 1024 * 1024 || file.size === 0) {
      setImageFile(null);
      setImageError(fr ? "Utilisez une image JPEG ou PNG, 5 Mo maximum." : "Choose a JPEG or PNG image, maximum 5 MB.");
      return;
    }
    setImageDigest("");
    setImageFile(file);
  };
  useEffect(() => { setLanguage(fr ? "fr" : "en"); }, [fr]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.allSettled([
      api("/api/home/spotlight/editor", { method: "GET" }),
      api("/api/home/editorial-schedule", { method: "GET" }),
      api("/api/public-homepage?language=en", { method: "GET" }),
      api("/api/public-homepage?language=fr", { method: "GET" }),
    ]).then((results) => {
      if (!active) return;
      const value = (i) => results[i].status === "fulfilled" ? results[i].value : null;
      setFeeds({
        spotlight: value(0), schedule: value(1), publicEn: value(2), publicFr: value(3),
      });
      if (!value(0) && !value(1) && !value(2) && !value(3)) {
        setLoadError(fr ? "Sources éditoriales indisponibles." : "Editorial sources unavailable.");
      } else setLoadError("");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fr]);

  const sources = useMemo(() => collectSources(feeds || {}), [feeds]);
  const selected = sources.find((item) => item.id === sourceId) || null;
  useEffect(() => {
    if (!sources.some((item) => item.id === sourceId)) setSourceId(sources[0]?.id || "");
  }, [sources, sourceId]);
  useEffect(() => {
    setCopy(makeCopy(selected, channel, language));
    setHeadline("");
    setNotice("");
  }, [selected, channel, language]);

  const reloadFacebook = async () => {
    const [status, posts] = await Promise.all([
      api("/api/home/facebook-publishing/status", { method: "GET" }),
      api("/api/home/facebook-publishing/posts", { method: "GET" })
    ]);
    setFbStatus(status);
    setFbHistory(Array.isArray(posts?.posts) ? posts.posts : []);
  };
  useEffect(() => {
    let live = true;
    if (channel !== "facebook") return;
    Promise.allSettled([
      api("/api/home/facebook-publishing/status", { method: "GET" }),
      api("/api/home/facebook-publishing/posts", { method: "GET" })
    ]).then(results => {
      if (!live) return;
      if (results[0].status === "fulfilled") setFbStatus(results[0].value);
      else setFbError(fr ? "Statut Facebook indisponible." : "Facebook publishing status unavailable.");
      if (results[1].status === "fulfilled") setFbHistory(results[1].value?.posts || []);
    });
    return () => { live = false; };
  }, [channel, fr]);
  // Facebook Page posts have no standalone headline: send the editorial title
  // as the first line of the exact approved message, followed by its body.
  const postText = sourceId === "announcement"
    ? [headline.trim(),copy.trim()].filter(Boolean).join("\n\n")
    : copy.trim();
  const postTextReady = postText.length > 0 && postText.length <= 3500;
  const mediaReady = !imageFile || !!imageDigest;
  const matchingPosts = fbHistory.filter(p => p.message === postText &&
    p.language === language && p.sourceKey === (sourceId || "") &&
    (p.imageSha256 || "") === imageDigest);
  const blockedAttempt = matchingPosts.find(p =>
    ["publishing","published","needs_review"].includes(p.status));
  const currentApproval = blockedAttempt ? null : matchingPosts.find(p => p.status === "approved") ||
    (fbApproved && fbApproved.message === postText &&
      fbApproved.language === language &&
      fbApproved.sourceKey === (sourceId || "") &&
      (fbApproved.imageSha256 || "") === imageDigest ? fbApproved : null);
  const existingDraft = matchingPosts.find(p => p.status === "draft");

  const runFacebook = async (job) => {
    setFbBusy(true); setFbError(""); setFbNotice("");
    try { await job(); }
    catch (e) {
      setFbError(e?.data?.message || e?.message || (fr ? "Action Facebook impossible." : "Facebook action failed."));
      await reloadFacebook().catch(() => {});
    } finally { setFbBusy(false); }
  };
  const checkFacebook = () => runFacebook(async () => {
    const result = await api("/api/home/facebook-publishing/check-connection",
      {method:"POST",body:JSON.stringify({})});
    if (!result?.ok || !result.page?.name) throw Error("Page verification failed.");
    setFbVerified(result.page);
    setFbNotice(""); // Page name is displayed exactly once below.
  });
  // One explicit steward action: validate the final copy and publish it.
  // The server always performs two ordered, role-checked steps: approve, publish.
  const approveAndPublishFacebook = () => {
    if (!postTextReady || !fbStatus?.configured || !fbVerified || blockedAttempt || !mediaReady || imageError) return;
    const question = fr
      ? "Approuver et publier maintenant ce contenu sur la Page Facebook officielle NextScenes ?"
      : "Approve and publish this exact text" + (imageFile ? " and attached image" : "") + " on the official NextScenes Facebook Page now?";
    if (!window.confirm(question)) return;
    return runFacebook(async () => {
      let id = currentApproval?.id;
      if (!id) {
        if (!existingDraft) {
          const payload=new FormData();
          payload.set("message",postText);
          payload.set("language",language);
          payload.set("sourceKey",sourceId || "");
          if (imageFile) payload.set("image",imageFile);
          const draft = await api("/api/home/facebook-publishing/drafts", {
            method:"POST",body:payload
          });
          id = draft.post.id;
        } else id = existingDraft.id;
        const approved = await api("/api/home/facebook-publishing/posts/" + id + "/approve",
          {method:"POST",body:JSON.stringify({})});
        if (!approved?.ok || approved.post?.status !== "approved") throw Error("Approval was not confirmed.");
        id=approved.post.id;
        setFbApproved({id,message:postText,language,sourceKey:sourceId || "",imageSha256:imageDigest});
      }
      const result = await api("/api/home/facebook-publishing/posts/" + id + "/publish",
        {method:"POST",body:JSON.stringify({})});
      if (!result?.ok || result.post?.status !== "published") throw Error("Publication not confirmed.");
      setFbNotice(fr ? "Publication confirmée par Facebook." : "Facebook accepted the publication.");
      setFbApproved(null);
      await reloadFacebook();
    });
  };

  // Recover an approved/draft record after a browser interruption. Publishing
  // always uses its immutable server-side text and image snapshot.
  const resumeExisting = (post) => {
    if (!fbVerified || !fbStatus?.configured ||
      !["draft","approved"].includes(post.status)) return;
    const question=fr
      ? "Approuver et publier cet enregistrement existant sur Facebook ?"
      : "Approve and publish this existing " + (post.hasImage ? "image and caption" : "text") + " to Facebook now?";
    if (!window.confirm(question)) return;
    return runFacebook(async () => {
      if (post.status === "draft") {
        const approved=await api("/api/home/facebook-publishing/posts/" + post.id + "/approve",{
          method:"POST",body:JSON.stringify({})
        });
        if(approved.post?.status!=="approved")throw Error("Approval was not confirmed.");
      }
      const result=await api("/api/home/facebook-publishing/posts/" + post.id + "/publish",{
        method:"POST",body:JSON.stringify({})
      });
      if (!result?.ok || result.post?.status!=="published")throw Error("Publication not confirmed.");
      setFbNotice(fr ? "Publication confirmée par Facebook." : "Facebook accepted the publication.");
      await reloadFacebook();
    });
  };

  const handleCopy = async () => {
    if (!postTextReady) return;
    try {
      await navigator.clipboard.writeText(postText);
      setNotice(fr ? "Texte copié. Vérifiez-le avant publication." : "Copied. Review before posting.");
    } catch {
      setNotice(fr ? "Copie indisponible : sélectionnez le texte." : "Copy unavailable. Select the text manually.");
    }
  };

  return <section className="ns-card" style={{ padding: 20 }}>
    <h2 style={{ marginTop: 0, fontSize: 21 }}>{fr ? "Préparation pour les réseaux sociaux" : "Social Media Publishing Kit"}</h2>
    <p className="ns-card-meta">
      {fr
        ? "Facebook : préparez, validez et publiez sur la Page officielle. Instagram et YouTube restent manuels pendant le pilote."
        : "Facebook pilot: prepare, approve, and publish to the official Page. Instagram and YouTube remain manual."}
    </p>
    {loadError && <p role="alert" style={{ color: "#9b2525" }}>{loadError}</p>}
    {loading ? <p>{fr ? "Chargement…" : "Loading editorial sources…"}</p> : null}
    <div role="group" aria-label={fr ? "Réseau social" : "Social channel"} style={{ display: "flex", flexWrap: "wrap", gap: 10, margin: "16px 0" }}>
      {Object.entries(CHANNELS).map(([key, value]) => <button key={key} type="button"
        aria-pressed={channel === key} onClick={() => setChannel(key)}
        style={{ ...action, display: "inline-flex", alignItems: "center", gap: 8,
          background: channel === key ? "#116f43" : "white", color: channel === key ? "white" : "#125638" }}>
        <SocialPlatformIconV1 platform={key} />{value.name}
      </button>)}
    </div>
    <div style={{ display: "grid", gap: 13, gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", margin: "18px 0" }}>
      <label style={{ display: "grid", gap: 6, fontWeight: 700 }}>
        {fr ? "Source éditoriale" : "Editorial source"}
        <select style={surface} value={sourceId} onChange={(e) => { setSourceId(e.target.value); clearImage(); }}>
          {sources.length === 0 && <option value="">{fr ? "Aucune source approuvée" : "No approved sources"}</option>}
          {sources.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
        </select>
      </label>
      <label style={{ display: "grid", gap: 6, fontWeight: 700 }}>
        {fr ? "Langue de la publication" : "Post language"}
        <select style={surface} value={language} onChange={(e) => setLanguage(e.target.value)}>
          <option value="en">English</option>
          <option value="fr">Français</option>
        </select>
      </label>
    </div>
    {selected && selected.id !== "announcement" && !clean(selected[language]?.title) &&
      <p role="status">{fr ? "Cette source n'a pas encore de texte dans cette langue." : "This source has no copy in the selected language yet."}</p>}
    {sourceId === "announcement" && <label style={{ display: "grid", gap: 7, fontWeight: 700, marginBottom: 13 }}>
      {fr ? "Titre / Accroche de la publication (facultatif)" : "Post Title / Headline (optional)"}
      <input type="text" style={surface} maxLength={120} value={headline}
        onChange={e => { setHeadline(e.target.value); setNotice(""); }}
        placeholder={fr ? "Saisissez le titre de votre annonce" : "Enter your announcement headline"}/>
      <span style={{fontSize:12,fontWeight:400,color:"#52675a"}}>
        {fr ? "Apparaîtra comme première ligne du texte publié sur Facebook." :
          "Appears as the first line of the Facebook post, not as a separate Facebook title field."}
      </span>
    </label>}
    <label style={{ display: "grid", gap: 7, fontWeight: 700 }}>
      {sourceId === "announcement" ? (fr ? "Texte de l’annonce" : "Announcement message") : (fr ? "Texte à relire et adapter" : "Review and edit your post")}
      <textarea rows={9} style={{ ...surface, resize: "vertical", lineHeight: 1.6 }}
        value={copy} onChange={(e) => { setCopy(e.target.value); setNotice(""); }}
        placeholder={fr ? "Rédigez une nouvelle annonce ou choisissez un contenu existant." : "Write an original announcement or choose existing editorial content."}/>
    </label>
    {sourceId === "announcement" && <div style={{marginTop:13,marginBottom:13}}>
      <strong style={{fontSize:14}}>{fr ? "Aperçu du texte final" : "Final post preview"}</strong>
      <pre style={{...surface,whiteSpace:"pre-wrap",overflowWrap:"anywhere",fontFamily:"inherit",fontWeight:400,margin:"8px 0"}}>
        {postText || (fr ? "Votre titre et votre texte apparaîtront ici." : "Your headline and message will appear here.")}
      </pre>
      <span role={postText.length>3500?"alert":"status"} style={{fontSize:12,color:postText.length>3500?"#992c2c":"#52675a"}}>
        {postText.length}/3500 {fr ? "caractères" : "characters"}
        {postText.length>3500 ? (fr ? " · Raccourcissez le message avant publication." : " · Shorten the message before publishing.") : ""}
      </span>
    </div>}
    {channel === "facebook" && <div style={{marginTop:14,display:"grid",gap:8}}>
      <label style={{fontWeight:700}}>
        {fr ? "Image (facultative : JPEG ou PNG, 5 Mo maximum)" : "Attach an image (optional: JPEG or PNG, maximum 5 MB)"}
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" style={{...surface,marginTop:7}}
          onChange={e => selectImage(e.target.files?.[0] || null)}/>
      </label>
      {imageError && <p role="alert" style={{color:"#992c2c",margin:0}}>{imageError}</p>}
      {imageFile && <div style={{display:"flex",gap:12,alignItems:"start",flexWrap:"wrap"}}>
        {imagePreview && <img alt={fr?"Aperçu de l'image jointe":"Selected image preview"}
          src={imagePreview} style={{maxWidth:240,maxHeight:220,objectFit:"contain",borderRadius:10,border:"1px solid #c9decf"}}/>}
        <div style={{display:"grid",gap:5}}>
          <span style={{fontSize:13}}>{imageFile.name} · {Math.ceil(imageFile.size/1024)} KB</span>
          {!mediaReady && <span style={{fontSize:12}}>{fr?"Vérification de l'image…":"Verifying image…"}</span>}
          <button type="button" style={action} onClick={clearImage}>
            {fr?"Retirer l'image":"Remove image"}
          </button>
        </div>
      </div>}
      <p style={{fontSize:12,color:"#587166",margin:"2px 0 8px"}}>
        {fr ? "Une image originale ou dont vous avez les droits sera publiée avec le texte approuvé." :
          "The selected image and caption are approved together. Use only images you own or have permission to publish."}
      </p>
    </div>}
    {channel === "facebook" && <div className="ns-card" style={{padding:16,marginTop:16,border:"1px solid #c8ddcd",background:"#f6faf7"}}>
      <h3 style={{margin:"0 0 8px",fontSize:17}}>{fr?"Publication directe sur Facebook":"Direct Facebook Page publishing"}</h3>
      <p style={{fontSize:13,color:"#52675a"}}>
        {fbStatus?.configured
          ? (fr?"Configuration du serveur détectée. Vérifiez la Page avant publication.":"Server credentials configured. Verify the Page before publishing.")
          : (fr?"La publication est désactivée jusqu'à la connexion officielle de la Page.":"Publishing is disabled until the official Facebook Page credentials and pilot flag are configured.")}
      </p>
      {fbError && <p role="alert" style={{color:"#9b2525"}}>{fbError}</p>}
      {fbNotice && <p role="status" style={{color:"#125f3d"}}>{fbNotice}</p>}
      {fbVerified && <p style={{fontSize:13}}><strong>{fr?"Page vérifiée :":"Verified Page:"}</strong> {fbVerified.name}</p>}
      <div style={{display:"flex",flexWrap:"wrap",gap:10,marginBottom:12}}>
        <button type="button" style={action} onClick={checkFacebook}
          disabled={fbBusy || !fbStatus?.configured}>{fr?"Vérifier la connexion":"Check Facebook connection"}</button>
        <button type="button" style={{...action,background:"#116f43",color:"#fff"}}
          onClick={approveAndPublishFacebook} disabled={fbBusy || !postTextReady || !fbStatus?.configured || !fbVerified || !!blockedAttempt || !mediaReady || !!imageError}>
          {fr?"Approuver et publier sur Facebook":"Approve & Publish to Facebook"}
        </button>
      </div>
      {blockedAttempt && <p role="alert" style={{fontSize:12,color:"#8c3737"}}>{
        fr ? "Ce texte a déjà été envoyé ou nécessite vérification. Aucune nouvelle tentative ne sera lancée." :
          "This exact post has already been attempted or published. Recheck the Page before preparing a different post."}
      </p>}
      {currentApproval && <p style={{fontSize:12,color:"#536d5f"}}>
        {fr?"Texte approuvé et verrouillé. Toute modification exige une nouvelle approbation.":"Approved text is locked. Any changes require new approval."}
      </p>}
      <h4 style={{margin:"18px 0 8px"}}>{fr?"Historique des publications":"Facebook publishing history"}</h4>
      {fbHistory.length===0 ? <p style={{fontSize:13}}>{fr?"Aucune publication enregistrée.":"No recorded publishing attempts."}</p> :
        <div style={{display:"grid",gap:7}}>
          {fbHistory.slice(0,8).map(post=><div key={post.id} style={{background:"white",padding:10,borderRadius:9,fontSize:13}}>
            <strong>{post.status === "needs_review" ? (fr?"À vérifier sur Facebook":"Needs manual reconciliation") : post.status}</strong>
            {" · "}{post.language.toUpperCase()}{" · "}{String(post.message||"").slice(0,90)}
            {post.hasImage ? " · 📷" : ""}
            {["draft","approved"].includes(post.status) && <button type="button" style={{...action,marginLeft:10,padding:"5px 9px"}}
              disabled={fbBusy || !fbVerified || !fbStatus?.configured}
              onClick={()=>resumeExisting(post)}>{fr?"Approuver et publier":"Approve & Publish"}</button>}
            {post.permalink && <a href={post.permalink} target="_blank" rel="noopener noreferrer" style={{marginLeft:10}}>
              {fr?"Voir la publication":"View post"} ↗</a>}
          </div>)}
        </div>}
      <p style={{fontSize:12,color:"#536d5f",marginBottom:0}}>
        {fr?"En cas de résultat incertain, vérifiez directement la Page. Le système ne renvoie jamais automatiquement un message.":"Unconfirmed attempts require manual Page reconciliation. The system never retries a possibly published message."}
      </p>
    </div>}
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginTop: 15 }}>
      <button type="button" style={action} onClick={handleCopy} disabled={!postTextReady}>
        {fr ? "Copier le texte" : "Copy post"}
      </button>
      <a style={action} href={CHANNELS[channel].url} target="_blank" rel="noopener noreferrer">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><SocialPlatformIconV1 platform={channel} />
          {fr ? "Ouvrir " : "Open "}{CHANNELS[channel].name} ↗</span>
      </a>
      {notice && <span role="status" style={{ fontSize: 13, color: "#146044" }}>{notice}</span>}
    </div>
    <p style={{ fontSize: 12, color: "#52675a", marginTop: 20 }}>
      {fr
        ? "Les identifiants Facebook restent sur le serveur. Vérifiez les droits des citations et illustrations. Aucune autre plateforme n'est connectée."
        : "Facebook credentials remain on the server. Check quotation and image rights. Other platforms are not connected; copying remains available as a fallback."}
    </p>
  </section>;
}
